import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos, Check, Close } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { MdCompareArrows } from "react-icons/md";

const operators = {
  equals: "=",
  notEquals: "!=",
  contains: "contém",
  greaterThan: ">",
  lessThan: "<",
  greaterOrEqual: ">=",
  lessOrEqual: "<=",
  startsWith: "começa com",
  endsWith: "termina com",
  isEmpty: "está vazio",
  isNotEmpty: "não está vazio",
  isTrue: "é verdadeiro",
  isFalse: "é falso",
};

function CaseNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const leftValue = data?.leftValue || "valor1";
  const operator = data?.operator || "equals";
  const rightValue = data?.rightValue || "valor2";

  const truncateText = (text, maxLength = 15) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `2px solid #9c27b0`,
        boxShadow: `0 4px 8px rgba(0, 0, 0, 0.15)`,
        position: "relative",
        transition: "all 0.2s ease",
        width: "auto",
        minWidth: "200px",
        maxWidth: "280px",
        boxSizing: "border-box",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          left: "-14px",
          top: "50%",
          transform: "translateY(-50%)",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #9c27b0 0%, #7b1fa2 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botoes de acao */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: theme.spacing(1),
          top: theme.spacing(1),
          gap: theme.spacing(1),
          cursor: "pointer",
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "16px",
            height: "16px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1.5),
        }}
      >
        <MdCompareArrows
          style={{
            width: "18px",
            height: "18px",
            color: "#9c27b0",
          }}
        />
        <div
          style={{
            color: theme.palette.text.primary,
            fontSize: "15px",
            fontWeight: 500,
          }}
        >
          Condição
        </div>
      </div>

      {/* Condição */}
      <div
        style={{
          backgroundColor: theme.palette.background.default,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1.5),
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.spacing(1),
            flexWrap: "wrap",
          }}
        >
          <Chip
            size="small"
            label={truncateText(leftValue)}
            title={leftValue}
            style={{
              backgroundColor: "rgba(156, 39, 176, 0.15)",
              color: "#9c27b0",
              fontWeight: 500,
              fontSize: "11px",
            }}
          />
          <Typography
            variant="body2"
            style={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
            }}
          >
            {operators[operator] || operator}
          </Typography>
          {operator !== "isEmpty" && operator !== "isNotEmpty" && operator !== "isTrue" && operator !== "isFalse" && (
            <Chip
              size="small"
              label={truncateText(rightValue)}
              title={rightValue}
              style={{
                backgroundColor: "rgba(156, 39, 176, 0.15)",
                color: "#9c27b0",
                fontWeight: 500,
                fontSize: "11px",
              }}
            />
          )}
        </div>
      </div>

      {/* Labels das saídas */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: theme.spacing(1.5),
          gap: theme.spacing(2),
          paddingRight: theme.spacing(1),
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Check style={{ color: "#4caf50", width: 14, height: 14 }} />
          <Typography variant="caption" style={{ color: "#4caf50", fontWeight: 500 }}>
            Sim
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Close style={{ color: "#f44336", width: 14, height: 14 }} />
          <Typography variant="caption" style={{ color: "#f44336", fontWeight: 500 }}>
            Não
          </Typography>
        </div>
      </div>

      {/* Handle de saída TRUE (cima) */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "35%",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #4caf50 0%, #388e3c 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <Check
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Handle de saída FALSE (baixo) */}
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          right: "-14px",
          top: "75%",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #f44336 0%, #d32f2f 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <Close
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(CaseNode);
